import { useState, useCallback } from 'react'

export type ToastType = 'success' | 'error' | 'info'

interface ToastState {
  msg: string
  type: ToastType
}

export function useToast(durationMs = 3000) {
  const [toast, setToast] = useState<ToastState | null>(null)

  const showToast = useCallback((msg: string, type: ToastType = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), durationMs)
  }, [durationMs])

  return { toast, showToast }
}

const COLORS: Record<ToastType, { bg: string; border: string }> = {
  success: { bg: '#f0fdf4', border: '#16a34a' },
  error:   { bg: '#fef2f2', border: '#dc2626' },
  info:    { bg: '#eff6ff', border: '#2563eb' },
}

const TEXT_COLORS: Record<ToastType, string> = {
  success: '#16a34a',
  error:   '#dc2626',
  info:    '#2563eb',
}

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error:   '✕',
  info:    'ℹ',
}

export function Toast({ toast }: { toast: ToastState | null }) {
  if (!toast) return null
  const { bg, border } = COLORS[toast.type]
  return (
    <div style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, backgroundColor: bg, border: `1.5px solid ${border}`,
      color: TEXT_COLORS[toast.type], padding: '11px 20px', borderRadius: 12,
      fontSize: 14, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      animation: 'fadeIn 0.15s ease',
    }}>
      <span style={{ fontSize: 15 }}>{ICONS[toast.type]}</span>
      {toast.msg}
    </div>
  )
}
